/**
 * TablePagination Component
 *
 * Standalone pagination controls that can be used with any table
 */

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { cn } from '@medsuite/ui-components';
import { Table } from '@tanstack/react-table';
import { forwardRef } from 'react';

export interface TablePaginationProps {
  table?: Table<unknown>;
  currentPage?: number;
  pageSize?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  showPageSizeSelector?: boolean;
  showPageInfo?: boolean;
  showFirstLast?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const TablePagination = forwardRef<HTMLDivElement, TablePaginationProps>(
  (
    {
      table,
      currentPage: controlledCurrentPage,
      pageSize: controlledPageSize,
      totalItems: controlledTotalItems,
      onPageChange,
      onPageSizeChange,
      pageSizeOptions = [10, 20, 50, 100],
      showPageSizeSelector = true,
      showPageInfo = true,
      showFirstLast = true,
      className,
      size = 'md',
    },
    ref
  ) => {
    // Support both controlled (via props) and TanStack Table usage
    const currentPage = table
      ? table.getState().pagination.pageIndex
      : (controlledCurrentPage ?? 0);
    const pageSize = table ? table.getState().pagination.pageSize : (controlledPageSize ?? 10);
    const totalItems = table
      ? table.getFilteredRowModel().rows.length
      : (controlledTotalItems ?? 0);
    const pageCount = Math.ceil(totalItems / pageSize);

    const canPreviousPage = currentPage > 0;
    const canNextPage = currentPage < pageCount - 1;

    const handlePageChange = (page: number) => {
      if (table) {
        table.setPageIndex(page);
      } else {
        onPageChange?.(page);
      }
    };

    const handlePageSizeChange = (newPageSize: number) => {
      if (table) {
        table.setPageSize(newPageSize);
      } else {
        onPageSizeChange?.(newPageSize);
      }
    };

    const startItem = currentPage * pageSize + 1;
    const endItem = Math.min((currentPage + 1) * pageSize, totalItems);

    const buttonSizeClasses = {
      sm: 'h-7 px-2 text-xs',
      md: 'h-8 px-3 text-sm',
      lg: 'h-10 px-4 text-base',
    }[size];

    const iconSizeClasses = {
      sm: 'h-3 w-3',
      md: 'h-4 w-4',
      lg: 'h-5 w-5',
    }[size];

    if (totalItems === 0) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col items-center justify-between space-y-3 sm:flex-row sm:space-y-0',
          className
        )}
      >
        {/* Left side: Page info and page size selector */}
        <div className="flex items-center space-x-4">
          {showPageInfo && (
            <div className={cn('text-gray-700', size === 'sm' ? 'text-xs' : 'text-sm')}>
              Showing <span className="font-medium">{startItem}</span> to{' '}
              <span className="font-medium">{endItem}</span> of{' '}
              <span className="font-medium">{totalItems}</span> results
            </div>
          )}

          {showPageSizeSelector && (
            <div className="flex items-center space-x-2">
              <label
                htmlFor="pageSize"
                className={cn('text-gray-700', size === 'sm' ? 'text-xs' : 'text-sm')}
              >
                Show
              </label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className={cn(
                  'rounded-md border border-gray-300 bg-white text-gray-900',
                  'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500',
                  buttonSizeClasses
                )}
              >
                {pageSizeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Right side: Pagination controls */}
        <div className="flex items-center space-x-2">
          {showFirstLast && (
            <button
              onClick={() => handlePageChange(0)}
              disabled={!canPreviousPage}
              className={cn(
                'rounded-md border font-medium transition-colors',
                buttonSizeClasses,
                canPreviousPage
                  ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  : 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
              )}
            >
              First
            </button>
          )}

          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={!canPreviousPage}
            className={cn(
              'inline-flex items-center justify-center rounded-md border font-medium transition-colors',
              buttonSizeClasses,
              canPreviousPage
                ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                : 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
            )}
          >
            <ChevronLeftIcon className={iconSizeClasses} />
            <span className="ml-1">Previous</span>
          </button>

          <div className={cn('text-gray-700', size === 'sm' ? 'text-xs' : 'text-sm')}>
            Page <span className="font-medium">{currentPage + 1}</span> of{' '}
            <span className="font-medium">{pageCount}</span>
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={!canNextPage}
            className={cn(
              'inline-flex items-center justify-center rounded-md border font-medium transition-colors',
              buttonSizeClasses,
              canNextPage
                ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                : 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
            )}
          >
            <span className="mr-1">Next</span>
            <ChevronRightIcon className={iconSizeClasses} />
          </button>

          {showFirstLast && (
            <button
              onClick={() => handlePageChange(pageCount - 1)}
              disabled={!canNextPage}
              className={cn(
                'rounded-md border font-medium transition-colors',
                buttonSizeClasses,
                canNextPage
                  ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  : 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
              )}
            >
              Last
            </button>
          )}
        </div>
      </div>
    );
  }
);

TablePagination.displayName = 'TablePagination';

/**
 * SimplePagination - Minimal pagination with just prev/next
 */
export interface SimplePaginationProps {
  currentPage: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  className?: string;
}

export const SimplePagination = forwardRef<HTMLDivElement, SimplePaginationProps>(
  ({ currentPage, pageCount, onPageChange, className }, ref) => {
    const canPreviousPage = currentPage > 0;
    const canNextPage = currentPage < pageCount - 1;

    return (
      <div ref={ref} className={cn('flex items-center justify-between', className)}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!canPreviousPage}
          className={cn(
            'inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium',
            canPreviousPage
              ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              : 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
          )}
        >
          <ChevronLeftIcon className="mr-2 h-4 w-4" />
          Previous
        </button>

        <span className="text-sm text-gray-700">
          Page {currentPage + 1} of {pageCount}
        </span>

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!canNextPage}
          className={cn(
            'inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium',
            canNextPage
              ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              : 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
          )}
        >
          Next
          <ChevronRightIcon className="ml-2 h-4 w-4" />
        </button>
      </div>
    );
  }
);

SimplePagination.displayName = 'SimplePagination';
